import { useCallback, useContext, useEffect, useState } from "react";
import { AppConfigContext } from "../context";

export interface DripHistoryItem {
    executionKey: string;
    timestamp: string;
    // Asset actually credited. For kySOL fallbacks this is the kySOL mint.
    mint: string;
    // Raw amount of `mint`, in that mint's own decimals.
    amount: string;
    // True when this payout was paid in kySOL instead of the chosen reward token.
    paidAsKySol?: boolean;
    // The reward token the user originally chose; only present on fallbacks.
    intendedMint?: string;
}

export type DripAlertType =
    | "reward_token_unroutable"
    | "reward_below_min_swap"
    | "reward_below_min_transfer";

export interface DripAlert {
    type: DripAlertType;
    // The user's CHOSEN reward token.
    mint: string;
    reason: string;
    paidAsKySol: boolean;
    // Raw amount; decimals depend on paidAsKySol (kySOL 9 when true; unspecified dust when false).
    amountRaw: string;
    since: string;
    lastSeen: string;
}

// One incentive-campaign payout, per epoch.
export interface DripIncentivePayout {
    campaignId: string;
    epoch: number;
    timestamp: string;
    mint: string;
    // Raw amount of `mint`, in the campaign's declared decimals.
    amount: string;
    signature?: string;
}

// A partner incentive campaign's payouts to this wallet. These are paid on top
// of the drip rewards and are NOT included in `history` or in
// `totalDistributedRawByMint` — the two streams are disjoint.
export interface DripIncentiveCampaign {
    campaignId: string;
    vault: string;
    mint: string;
    decimals: number;
    totalReceivedRaw: string;
    owedRaw: string;
    paidRaw: string;
    // Accrued but not yet paid out, so not part of what has been received.
    pendingRaw: string;
    cohortShareBps: number;
    lastEpoch: number;
    lastAmountRaw: string;
    lastPaidAt: string;
    history: DripIncentivePayout[];
}

export interface DripHistoryResponse {
    wallet: string;
    updatedAt: string;
    totalDistributedRawByMint: Record<string, string>;
    history: DripHistoryItem[];
    // Current alerts for this wallet; replaced every run, absent/empty => no problem.
    alerts?: DripAlert[];
    // Keyed by campaign id, e.g. "ansem-2026q3".
    incentives?: Record<string, DripIncentiveCampaign>;
}

export const useDripHistory = (wallet: string | null | undefined, enabled = true) => {
    const { apiUrl } = useContext(AppConfigContext);
    const [data, setData] = useState<DripHistoryResponse | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchHistory = useCallback(async () => {
        if (!enabled || !wallet || !apiUrl) {
            setData(null);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);
        try {
            const response = await fetch(`${apiUrl}/drip/history?wallet=${wallet}`);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            const payload: DripHistoryResponse = await response.json();
            setData(payload);
        } catch (err) {
            console.error("Error fetching drip history:", err);
            setData(null);
            setError("Error fetching drip history");
        } finally {
            setIsLoading(false);
        }
    }, [enabled, wallet, apiUrl]);

    useEffect(() => {
        fetchHistory();
    }, [fetchHistory]);

    return { data, isLoading, error, refetch: fetchHistory };
};
